import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from './utils/axiosConfig';
import { Table, Button, Form, Modal, Card, Row, Col, Badge, Spinner } from 'react-bootstrap';
import { useAuth } from './context/AuthContext';
import { hasPermission } from './utils/permissions';
import { isSuperAdmin } from './utils/superAdmin';
import debounce from 'lodash.debounce';
import { useMediaQuery } from 'react-responsive';
import { MEMBER_TERM } from './config/clubTerms';

// 建立 axios 實例，設置預設的 Authorization 標頭
const axiosInstance = axios.create();
axiosInstance.interceptors.request.use((config) => {
  config.headers.Authorization = `Bearer ${localStorage.getItem("token")}`;
  return config;
});

const LecturerTable = () => {
  // 必須先調用所有 hooks，再進行條件判斷
  const { user } = useAuth();
  
  // 設定狀態變數 - 必須在條件判斷之前調用所有 hooks
  const [lecturers, setLecturers] = useState([]); // 講師清單
  const [categories, setCategories] = useState([]); // 類別清單
  const [stores, setStores] = useState([]); // 社別清單
  const [loading, setLoading] = useState(false); // 加載狀態
  const [filter, setFilter] = useState(''); // 搜尋過濾
  const [page, setPage] = useState(1); // 當前頁碼
  const [sortConfig, setSortConfig] = useState({ key: 'l_id', direction: 'desc' }); // 排序設定
  const [hasMore, setHasMore] = useState(true); // 是否有更多資料
  const [showModal, setShowModal] = useState(false); // Modal 顯示狀態
  const [selectedLecturer, setSelectedLecturer] = useState(null); // 選中的講師資料
  const [successMessage, setSuccessMessage] = useState(''); // 成功訊息
  const [formData, setFormData] = useState({}); // 表單資料
  const isMobile = useMediaQuery({ maxWidth: 768 }); // 檢測是否為手機端
  const [viewMode, setViewMode] = useState('auto'); // 顯示模式
  const observer = useRef(); // IntersectionObserver 參考
  const lastRef = useRef(); // 最後一個表格項目參考

  // 從後端獲取講師類別
  const fetchLecturerCategories = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/lecturer-categories');
      setCategories(response.data);
    } catch (error) {
      // Handle error silently
    }
  }, []);

  // 從後端獲取社別資料
  const fetchStores = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/lecturer-stores');
      setStores(response.data);
    } catch (error) {
      // Handle error silently
    }
  }, []);

  // 從後端獲取講師清單，支援分頁和篩選條件
  const fetchLecturers = useCallback(async () => {
    if (loading) return;
    setLoading(true); // 設定為加載中
    try {
      const response = await axiosInstance.get('/lecturers', {
        params: { filter, page, sortKey: sortConfig.key, sortDirection: sortConfig.direction },
      });
      setLecturers((prev) => page === 1 ? response.data : [...prev, ...response.data]); // 判斷是否為第一頁
      setHasMore(response.data.length > 0); // 檢查是否有更多資料
    } catch (error) {
      // Handle error silently
    } finally {
      setLoading(false); // 結束加載狀態
    }
  }, [page, filter, sortConfig]);

  // 排序處理函數
  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc',
    }));
    resetData(); // 重置資料
  };

  // 重置資料及頁碼
  const resetData = () => {
    setLecturers([]);
    setPage(1);
  };

  // 刪除講師
  const handleDelete = async (id) => {
    if (window.confirm('確定要刪除這位講師嗎？')) {
      try {
        const response = await axiosInstance.delete(`/lecturers/${id}`);
        setLecturers((prev) => prev.filter((lecturer) => lecturer.l_id !== id));
        showMessage(response.data.message); // 顯示成功訊息
      } catch (error) {
        console.error('刪除講師錯誤:', error);
        if (error.response?.status === 403) {
          showMessage(`權限不足：${error.response.data.message || '您沒有刪除權限'}`, 'error');
        } else if (error.response?.status === 401) {
          showMessage('登入已過期，請重新登入', 'error');
        } else {
          showMessage('刪除講師時出現錯誤，請稍後重試', 'error');
        }
      }
    }
  };

  // 儲存或更新講師
  const handleSave = async () => {
    try {
      const url = selectedLecturer ? `/lecturers/${selectedLecturer.l_id}` : '/lecturers';
      const method = selectedLecturer ? 'put' : 'post';
      const response = await axiosInstance[method](url, formData);
      setLecturers((prev) =>
        selectedLecturer
          ? prev.map((lecturer) => (lecturer.l_id === selectedLecturer.l_id ? response.data : lecturer))
          : [...prev, response.data]
      );
      showMessage(response.data.message); // 顯示成功訊息
      setShowModal(false); // 關閉 Modal
    } catch (error) {
      console.error('儲存講師錯誤:', error);
      if (error.response?.status === 403) {
        showMessage(`權限不足：${error.response.data.message || '您沒有儲存權限'}`, 'error');
      } else if (error.response?.status === 401) {
        showMessage('登入已過期，請重新登入', 'error');
      } else {
        showMessage('儲存講師信息時出現錯誤，請稍後重試', 'error');
      }
    }
  };

  // 顯示成功訊息，3 秒後自動消失
  const showMessage = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // 開啟或關閉 Modal
  const openModal = (lecturer = null) => {
    setSelectedLecturer(lecturer);
    setFormData(lecturer || {});
    setShowModal(true);
  };

  // 字段配置，用於動態生成表格和表單欄位
  const fieldConfig = [
    { label: '編號', key: 'l_id' },
    { label: '類別', key: 'lm_name' },
    { label: '社別', key: 'store_name' },
    { label: '講師姓名', key: 'l_name' },
    { label: '區域', key: 'l_region' },
    { label: '經驗', key: 'l_experience' },
    { label: '目前職位', key: 'l_currentpost' },
    { label: '演講題目及特色', key: 'l_lecture_features' },
    { label: '可分人數', key: 'l_capacity' },
    { label: '講師網址', key: 'l_website' },
    { label: '性別', key: 'l_sex' },
    { label: '電話', key: 'l_tel' },
    { label: '傳真', key: 'l_fax' },
    { label: '手機', key: 'l_mobile' },
    { label: '備註', key: 'l_remark' }
  ];

  // 初始化載入講師類別和社別
  useEffect(() => { 
    fetchLecturerCategories(); 
    fetchStores();
  }, []);

  // 使用 debounce 延遲呼叫 fetchLecturers，防止頻繁請求
  const debounceFetchLecturers = useCallback(debounce(() => fetchLecturers(), 300), [page, filter, sortConfig]);
  useEffect(() => { debounceFetchLecturers(); }, [page, filter, sortConfig]);

  // 設置 IntersectionObserver 以實現無限滾動
  useEffect(() => {
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loading) {
        setPage((prevPage) => prevPage + 1);
      }
    });
    if (lastRef.current) observer.current.observe(lastRef.current);
  }, [hasMore, loading]);

  // 簡化權限：只有秘書能編輯
  const canEditRole = isSuperAdmin(user?.e_id) || user?.e_position === 17;
  const canViewRole = user?.e_group === 5 || canEditRole; // 社友(e_group=5)、管理員、秘書都可以查看

  // 添加統一的 Modal 表單樣式
  const tableStyles = `
    @media (max-width: 768px) {
      .modal .form-control,
      .modal .form-select {
        font-size: 16px !important;
        min-height: 48px !important;
        padding: 12px !important;
      }
      .modal .form-label {
        font-size: 16px !important;
        font-weight: 600;
        margin-bottom: 8px;
      }
      .modal textarea.form-control {
        min-height: 120px !important;
      }
    }
  `;

  // 渲染表格和表單
  return (
    <div className="container-fluid mt-4">
      <style>{tableStyles}</style>
      {/* 權限檢查 - 所有社友都可以查看，只有管理員和秘書可以編輯 */}
      {!canViewRole ? (
        <div className="alert alert-warning">
          <h4>存取被拒絕</h4>
          <p>您沒有權限存取此頁面。只有{MEMBER_TERM}、秘書或管理員才能查看講師資料。</p>
        </div>
      ) : (
        <>
          {/* 顯示權限提示 */}
          {!canEditRole && (
            <div className="alert alert-info">
              <h5><i className="fas fa-info-circle me-2"></i>瀏覽模式</h5>
              <p className="mb-0">您目前以瀏覽模式查看講師資料。如需新增、修改或刪除資料，請聯繫管理員或秘書。</p>
            </div>
          )}
          {successMessage && <div className="alert alert-success">{successMessage}</div>}

      {/* 搜尋和操作按鈕區域 */}
      <Row className="mb-3">
        <Col xs={12} className="mb-2">
          <Form.Control
            type="text"
            placeholder="搜尋講師資料..."
            value={filter}
            size={isMobile ? "lg" : "md"}
            onChange={(e) => { setFilter(e.target.value); resetData(); }}
          />
        </Col>
      </Row>

      <Row className="mb-3 g-2">
        <Col xs={4} md={2}>
          <Button
            variant={viewMode === 'auto' ? 'primary' : 'outline-primary'}
            size={isMobile ? "lg" : "sm"}
            className="w-100"
            onClick={() => setViewMode('auto')}
            title="自動切換"
          >
            📱 自動
          </Button>
        </Col>
        <Col xs={4} md={2}>
          <Button
            variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
            size={isMobile ? "lg" : "sm"}
            className="w-100"
            onClick={() => setViewMode('table')}
            title="表格檢視"
          >
            📊 表格
          </Button>
        </Col>
        <Col xs={4} md={2}>
          <Button
            variant={viewMode === 'card' ? 'primary' : 'outline-primary'}
            size={isMobile ? "lg" : "sm"}
            className="w-100"
            onClick={() => setViewMode('card')}
            title="卡片檢視"
          >
            🃏 卡片
          </Button>
        </Col>
        {canEditRole && (
          <Col xs={12} md={6} className="text-md-end">
            <Button
              variant="success"
              size={isMobile ? "lg" : "sm"}
              className={isMobile ? "w-100" : ""}
              onClick={() => openModal()}
            >
              ➕ 新增講師
            </Button>
          </Col>
        )}
      </Row>

      {/* 資料顯示區域 - 根據模式切換 */}
      {(viewMode === 'card' || (viewMode === 'auto' && isMobile)) ? (
        /* 卡片式佈局 - 適合手機端 */
        <Row>
          {lecturers.map((lecturer, index) => (
            <Col
              key={lecturer.l_id}
              xs={12}
              md={6}
              lg={4}
              className="mb-3"
              ref={index === lecturers.length - 1 ? lastRef : null}
            >
              <Card className="h-100 shadow-sm">
                <Card.Header className="bg-dark text-white d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">{lecturer.l_name}</h6>
                  <Badge bg="primary">#{lecturer.l_id}</Badge>
                </Card.Header>
                <Card.Body>
                  <Row className="g-2 small">
                    <Col xs={6}>
                      <strong>類別:</strong><br/>
                      <span className="text-muted">{lecturer.lm_name}</span>
                    </Col>
                    <Col xs={6}>
                      <strong>社別:</strong><br/>
                      <span className="text-muted">{lecturer.store_name || '-'}</span>
                    </Col>
                    <Col xs={6}>
                      <strong>區域:</strong><br/>
                      <span className="text-muted">{lecturer.l_region || '-'}</span>
                    </Col>
                    <Col xs={6}>
                      <strong>可分享人數:</strong><br/>
                      <span className="text-muted">{lecturer.l_capacity || '-'}</span>
                    </Col>
                    {lecturer.l_experience && (
                      <Col xs={12}>
                        <strong>經驗:</strong><br/>
                        <span className="text-muted small">{lecturer.l_experience}</span>
                      </Col>
                    )}
                    {lecturer.l_currentpost && (
                      <Col xs={12}>
                        <strong>目前職位:</strong><br/>
                        <span className="text-muted small">{lecturer.l_currentpost}</span>
                      </Col>
                    )}
                    {lecturer.l_lecture_features && (
                      <Col xs={12}>
                        <strong>演講題目及特色:</strong><br/>
                        <span className="text-muted small">{lecturer.l_lecture_features}</span>
                      </Col>
                    )}
                    <Col xs={6}>
                      <strong>電話:</strong><br/>
                      <span className="text-muted">{lecturer.l_tel}</span>
                    </Col>
                    <Col xs={6}>
                      <strong>手機:</strong><br/>
                      <span className="text-muted">{lecturer.l_mobile}</span>
                    </Col>
                    {lecturer.l_website && (
                      <Col xs={12}>
                        <strong>網址:</strong><br/>
                        <a href={lecturer.l_website} target="_blank" rel="noopener noreferrer" className="text-decoration-none small">
                          {lecturer.l_website}
                        </a>
                      </Col>
                    )}
                    {lecturer.l_remark && (
                      <Col xs={12}>
                        <strong>備註:</strong><br/>
                        <span className="text-muted small">{lecturer.l_remark}</span>
                      </Col>
                    )}
                  </Row>
                </Card.Body>
                <Card.Footer className="bg-transparent">
                  {canEditRole ? (
                    <div className="d-flex gap-2">
                      <Button
                        variant="info"
                        size={isMobile ? "lg" : "sm"}
                        className="flex-fill"
                        onClick={() => openModal(lecturer)}
                      >
                        編輯
                      </Button>
                      <Button
                        variant="danger"
                        size={isMobile ? "lg" : "sm"}
                        className="flex-fill"
                        onClick={() => handleDelete(lecturer.l_id)}
                      >
                        刪除
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center text-muted">
                      <small>瀏覽模式 - 僅供查看</small>
                    </div>
                  )}
                </Card.Footer>
              </Card>
            </Col>
          ))}
          {loading && (
            <Col xs={12} className="text-center py-4">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">加載中...</span>
              </Spinner>
            </Col>
          )}
        </Row>
      ) : (
        /* 表格式佈局 - 適合桌面端 */
        <div style={{ overflowX: 'auto' }}>
        <Table striped bordered hover variant="dark" className="table-striped table-dark" style={{ 
          tableLayout: 'auto',
          minWidth: '1600px'
        }}>
        <thead>
          <tr>
            <th onClick={() => handleSort('l_id')} style={{ width: '60px', cursor: 'pointer', fontSize: '14px' }}>編號</th>
            <th onClick={() => handleSort('lm_name')} style={{ width: '80px', cursor: 'pointer', fontSize: '14px' }}>類別</th>
            <th onClick={() => handleSort('store_name')} style={{ width: '80px', cursor: 'pointer', fontSize: '14px' }}>社別</th>
            <th onClick={() => handleSort('l_name')} style={{ minWidth: '120px', cursor: 'pointer', fontSize: '14px' }}>講師姓名</th>
            <th onClick={() => handleSort('l_region')} style={{ width: '100px', cursor: 'pointer', fontSize: '14px' }}>區域</th>
            <th onClick={() => handleSort('l_experience')} style={{ minWidth: '150px', cursor: 'pointer', fontSize: '14px' }}>經驗</th>
            <th onClick={() => handleSort('l_currentpost')} style={{ minWidth: '120px', cursor: 'pointer', fontSize: '14px' }}>目前職位</th>
            <th style={{ minWidth: '150px', fontSize: '14px' }}>演講題目及特色</th>
            <th onClick={() => handleSort('l_capacity')} style={{ width: '80px', cursor: 'pointer', fontSize: '14px' }}>可分人數</th>
            <th onClick={() => handleSort('l_website')} style={{ minWidth: '120px', cursor: 'pointer', fontSize: '14px' }}>講師網址</th>
            <th onClick={() => handleSort('l_tel')} style={{ width: '100px', cursor: 'pointer', fontSize: '14px' }}>電話</th>
            <th onClick={() => handleSort('l_mobile')} style={{ width: '100px', cursor: 'pointer', fontSize: '14px' }}>手機</th>
            <th style={{ minWidth: '100px', fontSize: '14px' }}>備註</th>
            <th style={{ width: '80px', fontSize: '14px' }}>操作</th>
          </tr>
        </thead>
        <tbody>
          {lecturers.map((lecturer, index) => (
            <tr key={lecturer.l_id} ref={index === lecturers.length - 1 ? lastRef : null}>
              <td style={{ textAlign: 'center', fontSize: '14px', padding: '6px' }}>{lecturer.l_id}</td>
              <td style={{ fontSize: '12px', padding: '6px' }}>{lecturer.lm_name}</td>
              <td style={{ fontSize: '12px', padding: '6px' }}>{lecturer.store_name || '-'}</td>
              <td style={{ 
                wordBreak: 'break-word', 
                fontSize: '13px',
                padding: '6px',
                fontWeight: '500'
              }}>
                {lecturer.l_name}
              </td>
              <td style={{ fontSize: '12px', padding: '6px' }}>{lecturer.l_region || '-'}</td>
              <td style={{ 
                wordBreak: 'break-word', 
                fontSize: '12px',
                padding: '6px'
              }}>
                {lecturer.l_experience}
              </td>
              <td style={{ 
                wordBreak: 'break-word', 
                fontSize: '12px',
                padding: '6px'
              }}>
                {lecturer.l_currentpost}
              </td>
              <td style={{ 
                wordBreak: 'break-word', 
                fontSize: '11px',
                padding: '6px',
                maxWidth: '150px',
                lineHeight: '1.3'
              }}>
                {lecturer.l_lecture_features || '-'}
              </td>
              <td style={{ textAlign: 'center', fontSize: '12px', padding: '6px' }}>
                {lecturer.l_capacity || '-'}
              </td>
              <td style={{ 
                wordBreak: 'break-all',
                fontSize: '11px',
                padding: '6px',
                maxWidth: '120px'
              }}>
                {lecturer.l_website ? (
                  <a href={lecturer.l_website} target="_blank" rel="noopener noreferrer" 
                     style={{ color: '#17a2b8', textDecoration: 'none' }}>
                    {lecturer.l_website}
                  </a>
                ) : '-'}
              </td>
              <td style={{ fontSize: '12px', padding: '6px' }}>{lecturer.l_tel}</td>
              <td style={{ fontSize: '12px', padding: '6px' }}>{lecturer.l_mobile}</td>
              <td style={{ 
                wordBreak: 'break-word', 
                fontSize: '11px',
                padding: '6px',
                maxWidth: '100px'
              }}>
                {lecturer.l_remark}
              </td>
              <td style={{ 
                textAlign: 'center', 
                padding: '6px',
                whiteSpace: 'nowrap'
              }}>
                {canEditRole ? (
                  <>
                    <Button 
                      variant="info" 
                      size="sm" 
                      style={{ marginRight: '2px', fontSize: '11px', padding: '4px 8px' }}
                      onClick={() => openModal(lecturer)}
                    >
                      編輯
                    </Button>
                    <Button 
                      variant="danger" 
                      size="sm" 
                      style={{ fontSize: '11px', padding: '4px 8px' }}
                      onClick={() => handleDelete(lecturer.l_id)}
                    >
                      刪除
                    </Button>
                  </>
                ) : (
                  <span className="text-muted small">瀏覽模式</span>
                )}
              </td>
            </tr>
          ))}
          {loading && <tr><td colSpan="14" className="text-center">加載中...</td></tr>}
        </tbody>
      </Table>
      </div>
      )}

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        fullscreen={isMobile ? true : false}
        size={isMobile ? undefined : "lg"}
      >
        <Modal.Header closeButton>
          <Modal.Title>{selectedLecturer ? '編輯講師' : '新增講師'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {/* 類別下拉選單 */}
            <Form.Group className="mb-3">
              <Form.Label>講師類別</Form.Label>
              <Form.Control
                as="select"
                value={formData.l_group || ''}
                onChange={(e) => setFormData({ ...formData, l_group: e.target.value })}
              >
                <option value="">選擇類別</option>
                {categories.map((category) => (
                  <option key={category.lm_id} value={category.lm_id}>
                    {category.lm_name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            
            {/* 社別下拉選單 */}
            <Form.Group className="mb-3">
              <Form.Label>社別</Form.Label>
              <Form.Control
                as="select"
                value={formData.l_store_id || ''}
                onChange={(e) => setFormData({ ...formData, l_store_id: e.target.value })}
              >
                <option value="">選擇社別</option>
                {stores.map((store) => (
                  <option key={store.ss_id} value={store.ss_id}>
                    {store.ss_name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>講師姓名</Form.Label>
              <Form.Control
                type="text"
                value={formData.l_name || ''}
                onChange={(e) => setFormData({ ...formData, l_name: e.target.value })}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>區域</Form.Label>
              <Form.Control
                type="text"
                value={formData.l_region || ''}
                onChange={(e) => setFormData({ ...formData, l_region: e.target.value })}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>經驗</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.l_experience || ''}
                onChange={(e) => setFormData({ ...formData, l_experience: e.target.value })}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>目前職位</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.l_currentpost || ''}
                onChange={(e) => setFormData({ ...formData, l_currentpost: e.target.value })}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>演講題目及特色</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.l_lecture_features || ''}
                onChange={(e) => setFormData({ ...formData, l_lecture_features: e.target.value })}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>可分人數</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={formData.l_capacity || ''}
                onChange={(e) => setFormData({ ...formData, l_capacity: e.target.value })}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>講師網址</Form.Label>
              <Form.Control
                type="url"
                value={formData.l_website || ''}
                onChange={(e) => setFormData({ ...formData, l_website: e.target.value })}
                placeholder="https://example.com"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>性別</Form.Label>
              <Form.Control
                as="select"
                value={formData.l_sex || ''}
                onChange={(e) => setFormData({ ...formData, l_sex: e.target.value })}
              >
                <option value="">選擇性別</option>
                <option value="1">男</option>
                <option value="2">女</option>
              </Form.Control>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>電話</Form.Label>
              <Form.Control
                type="text"
                value={formData.l_tel || ''}
                onChange={(e) => setFormData({ ...formData, l_tel: e.target.value })}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>傳真</Form.Label>
              <Form.Control
                type="text"
                value={formData.l_fax || ''}
                onChange={(e) => setFormData({ ...formData, l_fax: e.target.value })}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>手機</Form.Label>
              <Form.Control
                type="text"
                value={formData.l_mobile || ''}
                onChange={(e) => setFormData({ ...formData, l_mobile: e.target.value })}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>備註</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.l_remark || ''}
                onChange={(e) => setFormData({ ...formData, l_remark: e.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size={isMobile ? "lg" : "sm"}
            className={isMobile ? "flex-fill" : ""}
            onClick={() => setShowModal(false)}
          >
            取消
          </Button>
          <Button
            variant="primary"
            size={isMobile ? "lg" : "sm"}
            className={isMobile ? "flex-fill" : ""}
            onClick={handleSave}
          >
            儲存
          </Button>
        </Modal.Footer>
      </Modal>
        </>
      )}
    </div>
  );
};

export default LecturerTable;
